import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { GiniusWordmark } from "../components/GiniusWordmark";
import {
  Play, Pause, SkipForward, SkipBack, Volume2, VolumeX, X, Maximize2,
  Building2, AlertTriangle, Users, Camera, Send, ClipboardCheck, FileText,
  BellRing, ShieldCheck, BarChart3, Wrench, RotateCcw, ArrowRight, Home, HardHat, ArrowLeft,
} from "lucide-react";

const IMG = {
  condominio: "https://static.prod-images.emergentagent.com/jobs/5cfa0dc9-e55e-4fe9-acd0-dc766fd8f72f/images/de5826a755356ef8d100f334a6319a36ae12f08aeef99e5dbc4147f30c046246.png",
  segnalazione: "https://static.prod-images.emergentagent.com/jobs/5cfa0dc9-e55e-4fe9-acd0-dc766fd8f72f/images/9d6c31b224e3c3127b789c61d360c73154805a63c15ead11589e796b58c23889.png",
  affido: "https://static.prod-images.emergentagent.com/jobs/5cfa0dc9-e55e-4fe9-acd0-dc766fd8f72f/images/881de3bbc0ccc3cb4ae08522b3eed46a046bf8cbe043d3e72f155d8006f9003f.png",
  report: "https://static.prod-images.emergentagent.com/jobs/5cfa0dc9-e55e-4fe9-acd0-dc766fd8f72f/images/2724e57c38e228d7a9abefb06d55b4a04e360192e6cc9c448656a761da4a0b49.png",
};

const closing = (title, subtitle) => ({
  id: "closing", seconds: 11, icon: ShieldCheck,
  eyebrow: "Ginius by Amministrazioni Izzo", title, subtitle, bullets: [], cta: true,
});

const SCENES_BY_AUDIENCE = {
  condomino: [
    {
      id: "intro", seconds: 10, image: IMG.condominio, icon: Home,
      eyebrow: "Per i condòmini",
      title: "Il tuo condominio, sempre sotto controllo",
      subtitle: "Segnala un guasto, segui la riparazione e tieni aggiornati i tuoi dati: tutto dal telefono, in pochi tocchi.",
      bullets: [],
    },
    {
      id: "segnala", seconds: 14, image: IMG.segnalazione, icon: Camera,
      eyebrow: "Segnalare un guasto",
      title: "Un guasto? Segnalalo in un minuto",
      subtitle: "Apri l'app, descrivi il problema e allega una foto. Sei guidato passo dopo passo.",
      bullets: [
        "Foto del guasto direttamente dallo smartphone",
        "Categoria e priorità suggerite in automatico",
        "Consenso privacy raccolto in modo semplice e conforme",
      ],
    },
    {
      id: "segui", seconds: 14, image: IMG.affido, icon: Send,
      eyebrow: "Segui la pratica",
      title: "Sai sempre a che punto è",
      subtitle: "Quando lo Studio affida l'intervento, ricevi i recapiti del fornitore e gli aggiornamenti in tempo reale.",
      bullets: [
        "Notifiche via email e in-app",
        "Vedi chi se ne occupa e i suoi contatti",
        "Cronologia trasparente di ogni passaggio",
      ],
    },
    {
      id: "chiusura", seconds: 12, icon: BellRing,
      eyebrow: "Conclusione",
      title: "Dalla manutenzione alla soluzione",
      subtitle: "Al termine ricevi la conferma di chiusura e puoi lasciare un feedback sul servizio.",
      bullets: [
        "Avvisi automatici sullo stato dell'intervento",
        "Ricevuta dell'intervento eseguito",
        "Valutazione del servizio in un tocco",
      ],
    },
    {
      id: "rac", seconds: 13, image: IMG.report, icon: ShieldCheck,
      eyebrow: "I tuoi dati",
      title: "Aggiorna la tua scheda anagrafica",
      subtitle: "Gestisci in autonomia i tuoi dati per il Registro Anagrafe Condominiale (RAC), nel rispetto del GDPR.",
      bullets: [
        "Consensi e preferenze fiscali sempre aggiornati",
        "Numero di occupanti e recapiti modificabili",
        "Privacy garantita in ogni passaggio",
      ],
    },
    closing("Più semplice per te, più sereno il condominio",
      "Una gestione trasparente e a misura di condòmino."),
  ],

  studio: [
    {
      id: "intro", seconds: 10, image: IMG.condominio, icon: Building2,
      eyebrow: "Per lo Studio e gli amministratori",
      title: "La regia di tutte le manutenzioni",
      subtitle: "Una sola piattaforma per smistare richieste, gestire fornitori e rendicontare, su tutti i condomìni.",
      bullets: [],
    },
    {
      id: "soluzione", seconds: 13, icon: Users,
      eyebrow: "Multi-ruolo",
      title: "Condòmini, fornitori e collaboratori in un flusso unico",
      subtitle: "Ognuno nella sua area dedicata, lo Studio coordina il tutto da un'unica console.",
      bullets: [
        "Manutenzioni raccolte e organizzate automaticamente",
        "Anagrafiche di stabili, condòmini e fornitori",
        "Collaboratori dello Studio con accesso completo",
      ],
    },
    {
      id: "affido", seconds: 13, image: IMG.affido, icon: Send,
      eyebrow: "Smistamento e affido",
      title: "Affida al fornitore giusto con un clic",
      subtitle: "Assegni la richiesta dal tuo elenco fornitori; tutti vengono avvisati immediatamente.",
      bullets: [
        "Notifiche automatiche email e in-app",
        "Il condòmino vede a chi è affidato l'intervento",
        "Tracciamento completo di ogni operazione",
      ],
    },
    {
      id: "preventivi", seconds: 14, icon: FileText,
      eyebrow: "Preventivi",
      title: "Dal preventivo alla manutenzione, in automatico",
      subtitle: "Richiedi un preventivo, il fornitore risponde dall'app. Aderendo all'offerta…",
      bullets: [
        "Il preventivo viene trasmesso e accettato",
        "Si trasforma in una manutenzione affidata",
        "Importo e categoria già compilati per te",
      ],
    },
    {
      id: "sla", seconds: 12, icon: BellRing,
      eyebrow: "Scadenze e SLA",
      title: "Mai più scadenze dimenticate",
      subtitle: "Alert automatici sui ticket in ritardo e chiusura con ricevuta PDF.",
      bullets: [
        "Scadenze calcolate in base alla priorità",
        "Avvisi automatici per gli interventi in ritardo",
        "Ricevute PDF e storico costi",
      ],
    },
    {
      id: "report", seconds: 14, image: IMG.report, icon: BarChart3,
      eyebrow: "Trasparenza e conformità",
      title: "Report, statistiche e Registro Anagrafe",
      subtitle: "Tutto pronto da condividere e conforme alle normative.",
      bullets: [
        "Report periodici e statistiche con grafici",
        "RAC — Registro Anagrafe Condominiale esportabile",
        "Conformità GDPR in ogni passaggio",
      ],
    },
    closing("Gestione professionale, tutto sotto controllo",
      "Meno tempo a coordinare, più tempo per i tuoi condomìni."),
  ],

  fornitore: [
    {
      id: "intro", seconds: 10, image: IMG.affido, icon: HardHat,
      eyebrow: "Per i fornitori",
      title: "Lavora con lo Studio in modo organizzato",
      subtitle: "Ricevi incarichi chiari, invia preventivi e aggiorna lo stato degli interventi: tutto in un posto.",
      bullets: [],
    },
    {
      id: "incarichi", seconds: 13, image: IMG.segnalazione, icon: Send,
      eyebrow: "Incarichi",
      title: "Ricevi incarichi completi di tutti i dettagli",
      subtitle: "Condominio, unità, descrizione e foto del guasto: hai subito tutto ciò che ti serve.",
      bullets: [
        "Notifica immediata via email e in-app",
        "Foto e recapiti del referente sempre disponibili",
        "Priorità e scadenza ben evidenziate",
      ],
    },
    {
      id: "preventivi", seconds: 14, icon: FileText,
      eyebrow: "Preventivi",
      title: "Invia il tuo preventivo dall'app",
      subtitle: "Rispondi alle richieste dello Studio con tempistiche, note ed eventuale allegato PDF.",
      bullets: [
        "Disponibilità e tempistiche in pochi tocchi",
        "Allega il tuo preventivo in PDF",
        "Se accettato, diventa subito un incarico di manutenzione",
      ],
    },
    {
      id: "lavorazione", seconds: 13, icon: ClipboardCheck,
      eyebrow: "Gestione intervento",
      title: "Aggiorna lo stato in tempo reale",
      subtitle: "Accetta l'incarico, segnala l'inizio dei lavori e la conclusione: lo Studio è sempre allineato.",
      bullets: [
        "Accetta o rifiuta con un tocco",
        "Avvia e completa l'intervento",
        "Tutto tracciato nella cronologia",
      ],
    },
    {
      id: "chiusura", seconds: 12, image: IMG.report, icon: Wrench,
      eyebrow: "Conclusione",
      title: "Chiusura tracciata e storico interventi",
      subtitle: "Ogni lavoro completato resta documentato, con ricevute a portata di mano.",
      bullets: [
        "Storico completo dei tuoi interventi",
        "Ricevute e documenti sempre consultabili",
        "Rapporto di fiducia con lo Studio",
      ],
    },
    closing("Più incarichi, meno burocrazia",
      "Un canale diretto e ordinato con Ginius by Amministrazioni Izzo."),
  ],
  studio_promo: [
    {
      id: "intro", seconds: 11, image: IMG.condominio, icon: Building2,
      eyebrow: "Per gli Studi di amministrazione",
      title: "Ginius: il tuo Studio, finalmente digitale",
      subtitle: "La piattaforma completa per le manutenzioni condominiali: dalla manutenzione del condòmino alla chiusura con il fornitore, tutto tracciato e ordinato.",
      bullets: [],
    },
    {
      id: "flusso", seconds: 14, image: IMG.segnalazione, icon: Send,
      eyebrow: "Flusso guidato",
      title: "Dalla manutenzione all'affido, senza telefonate",
      subtitle: "I condòmini segnalano con foto, tu affidi al fornitore giusto con un clic. Tutti ricevono notifiche automatiche via email e in-app.",
      bullets: [
        "Manutenzioni guidate con foto e priorità",
        "Affido al fornitore e accettazione incarico online",
        "Cronologia completa di ogni intervento",
      ],
    },
    {
      id: "gestione", seconds: 14, image: IMG.affido, icon: FileText,
      eyebrow: "Tutto in un posto",
      title: "Preventivi, sinistri, RAC e anagrafiche",
      subtitle: "Gestisci preventivi e adesioni, denunce di sinistro con dati polizza, il Registro di Anagrafe Condominiale e le anagrafiche di stabili, condòmini e fornitori.",
      bullets: [
        "Preventivi richiesti e accettati dall'app",
        "Sinistri con denuncia PDF e dati assicurativi",
        "RAC conforme all'art. 1130 n.6 c.c.",
      ],
    },
    {
      id: "vantaggi", seconds: 14, image: IMG.report, icon: BarChart3,
      eyebrow: "I vantaggi per lo Studio",
      title: "Meno tempo perso, più professionalità",
      subtitle: "Report e statistiche automatici, scadenze sotto controllo, comunicazioni in blocco e piena conformità GDPR. Un'immagine moderna verso i tuoi condòmini.",
      bullets: [
        "Report periodici e statistiche automatiche",
        "Bacheca avvisi e comunicazioni ai fornitori",
        "GDPR e archivio consensi · PWA installabile",
      ],
    },
    {
      id: "trial", seconds: 14, icon: ShieldCheck,
      eyebrow: "Provala gratis",
      title: "30 giorni di prova, senza impegno",
      subtitle: "Attiviamo per te uno spazio dati riservato, separato da ogni altro Studio, per testare Ginius con i tuoi dati reali.",
      bullets: [
        "30 giorni gratuiti, spazio dati isolato e sicuro",
        "Limiti iniziali (es. 1 condominio, 20 fornitori) ampliabili su richiesta",
        "Sezioni attivabili su misura per le tue esigenze",
      ],
    },
    {
      id: "closing", seconds: 13, icon: ShieldCheck,
      eyebrow: "Ginius by Amministrazioni Izzo",
      title: "Richiedi ora la tua prova gratuita",
      subtitle: "Compila il modulo: ti contatteremo per attivare il tuo accesso di prova.",
      bullets: [], cta: true, ctaProva: true,
    },
  ],

  credius: [
    {
      id: "intro", seconds: 12, image: IMG.affido, icon: FileText,
      eyebrow: "Credius · Il modulo legale",
      title: "Pratiche legali di condominio, ovunque tu sia",
      subtitle: "Studio, avvocato affiliato e cliente lavorano sulla stessa pratica: recupero crediti, contenziosi, sinistri. Tutto tracciato, riservato dove serve.",
      bullets: [
        "3 ruoli, 1 unico spazio: Studio, Avvocato, Cliente",
        "Stati chiari: Da affidare · Preventivo · Affidata · In gestione · Conclusa",
        "Documenti e messaggi con riservatezza avvocato-cliente",
      ],
    },
    {
      id: "studio", seconds: 14, image: IMG.report, icon: Building2,
      eyebrow: "Lato Studio",
      title: "Apri una pratica in 60 secondi",
      subtitle: "Scegli tipo (stragiudiziale/giudiziale/sinistro), aggiungi clienti e controparti, seleziona un avvocato affiliato oppure salva senza affidare per proporlo prima al cliente.",
      bullets: [
        "Trasforma 1-click in fase Preventivo per l'ok del cliente",
        "Affida, revoca, cambia avvocato: sempre in cronologia",
        "Marca documenti e messaggi come '🔒 Riservati' con lo studio legale",
      ],
    },
    {
      id: "avvocato", seconds: 14, image: IMG.affido, icon: Wrench,
      eyebrow: "Lato Avvocato affiliato",
      title: "Ricevi l'incarico e operi in mobilità",
      subtitle: "Notifica push + email quando ti viene affidata una pratica. Accetti o rifiuti dal telefono. Registri attività, alleghi atti, chatti con Studio e cliente.",
      bullets: [
        "Timeline attività, udienze e provvedimenti",
        "Spese, onorari, IVA e CPA con pro-forma PDF",
        "Un solo profilo per più Studi tramite affiliazione",
      ],
    },
    {
      id: "cliente", seconds: 14, image: IMG.condominio, icon: Users,
      eyebrow: "Lato Cliente Credius",
      title: "Segui le tue vertenze in sola lettura",
      subtitle: "Sei condomino o fornitore anagrafato come cliente? Nella tua app compare 'Credius · Le mie pratiche' con documenti visibili e chat diretta con Studio e avvocato.",
      bullets: [
        "Solo documenti e messaggi visibili — mai la corrispondenza interna",
        "Chat allo Studio e all'avvocato con notifica istantanea",
        "Riservatezza garantita: le note strategiche restano tra Studio e Avvocato",
      ],
    },
    {
      id: "notifiche", seconds: 12, image: IMG.report, icon: BellRing,
      eyebrow: "Notifiche intelligenti",
      title: "Digest settimanale ogni lunedì mattina",
      subtitle: "Ricevi notifiche istantanee su ogni novità visibile e, ogni lunedì alle 08:00, un riepilogo email con tutte le attività dell'ultima settimana su ogni pratica.",
      bullets: [
        "Push e email in tempo reale per documenti e messaggi",
        "Riepilogo settimanale unico anche con più pratiche",
        "Zero rumore: doc/messaggi riservati non generano notifica al cliente",
      ],
    },
    closing("Credius — la sezione legale integrata di Ginius",
      "Studio, Avvocato e Cliente sulla stessa pratica.\nRiservato dove serve, condiviso dove conviene."),
  ],
};

const AUDIENCES = [
  { key: "condomino", label: "Per i condòmini", icon: Home, desc: "Segnalare guasti, seguire le pratiche, aggiornare i propri dati" },
  { key: "studio", label: "Per lo Studio / Amministratori", icon: Building2, desc: "Smistare, affidare, gestire preventivi e rendicontare" },
  { key: "fornitore", label: "Per i fornitori", icon: HardHat, desc: "Ricevere incarichi, inviare preventivi, gestire gli interventi" },
  { key: "studio_promo", label: "Per altri Studi", icon: Building2, desc: "Presentazione commerciale: funzioni, vantaggi e prova gratuita" },
  { key: "credius", label: "Credius · Modulo legale", icon: FileText, desc: "Studio · Avvocato · Cliente sulla stessa pratica: pratiche legali, riservatezza e digest settimanale" },
];

/* Musica di sottofondo generativa (Web Audio API) — nessun file esterno */
function createAmbientPlayer() {
  let ctx = null, master = null, timer = null, stopped = true, idx = 0;
  const chords = [
    [196.0, 246.94, 293.66], [174.61, 220.0, 261.63],
    [146.83, 196.0, 246.94], [164.81, 207.65, 261.63],
  ];
  const playChord = (freqs) => {
    if (!ctx) return;
    const t = ctx.currentTime;
    freqs.forEach((f, i) => {
      const o = ctx.createOscillator(); o.type = "sine"; o.frequency.value = f;
      const o2 = ctx.createOscillator(); o2.type = "triangle"; o2.frequency.value = f * 2;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.linearRampToValueAtTime(0.13 - i * 0.02, t + 2.2);
      g.gain.linearRampToValueAtTime(0.0001, t + 6.8);
      const g2 = ctx.createGain(); g2.gain.value = 0.035;
      const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 1100;
      o.connect(g); o2.connect(g2); g2.connect(g); g.connect(lp); lp.connect(master);
      o.start(t); o2.start(t); o.stop(t + 7.2); o2.stop(t + 7.2);
    });
  };
  return {
    start() {
      if (!stopped) return;
      const AC = window.AudioContext || window.webkitAudioContext;
      ctx = new AC(); master = ctx.createGain(); master.gain.value = 0.45; master.connect(ctx.destination);
      stopped = false;
      const loop = () => { if (stopped) return; playChord(chords[idx % chords.length]); idx++; timer = setTimeout(loop, 5200); };
      loop();
    },
    stop() { stopped = true; if (timer) clearTimeout(timer); if (ctx) { try { ctx.close(); } catch (e) {} ctx = null; } },
    setMuted(m) { if (master && ctx) master.gain.setTargetAtTime(m ? 0 : 0.45, ctx.currentTime, 0.2); },
  };
}

export default function Presentazione() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [audience, setAudience] = useState(null);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [muted, setMuted] = useState(false);
  const music = useRef(null);
  const rootRef = useRef(null);

  // Filmati disponibili in base al ruolo:
  //   condòmino → solo il filmato "condomino" (rimosso "Credius modulo legale")
  //   fornitore → filmato fornitore + Credius
  //   Studio/pubblico → tutti
  const allowedKeys = user?.role === "condomino" ? ["condomino"]
    : user?.role === "fornitore" ? ["fornitore", "credius"]
    : ["condomino", "studio", "fornitore", "studio_promo", "credius"];
  const audiences = AUDIENCES.filter((a) => allowedKeys.includes(a.key));
  const canChangeAudience = audiences.length > 1;

  const scenes = audience ? SCENES_BY_AUDIENCE[audience] : [];
  const scene = scenes[index];

  useEffect(() => () => { if (music.current) music.current.stop(); }, []);

  // Deep-link: /presentazione?p=studio_promo apre direttamente la traccia indicata
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("p");
    if (p && SCENES_BY_AUDIENCE[p] && allowedKeys.includes(p)) {
      setAudience(p); setIndex(0); setPaused(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!audience || paused) return undefined;
    const ms = (scenes[index].seconds || 12) * 1000;
    const t = setTimeout(() => {
      setIndex((i) => (i + 1 < scenes.length ? i + 1 : i));
      if (index + 1 >= scenes.length) setPaused(true);
    }, ms);
    return () => clearTimeout(t);
  }, [audience, paused, index]); // eslint-disable-line

  const begin = useCallback((key) => {
    if (!music.current) { music.current = createAmbientPlayer(); music.current.start(); }
    music.current.setMuted(muted);
    setAudience(key);
    setIndex(0);
    setPaused(false);
    if (rootRef.current?.requestFullscreen) rootRef.current.requestFullscreen().catch(() => {});
  }, [muted]);

  const toggleMute = () => { setMuted((m) => { const nm = !m; if (music.current) music.current.setMuted(nm); return nm; }); };
  const next = () => setIndex((i) => Math.min(i + 1, scenes.length - 1));
  const prev = () => setIndex((i) => Math.max(i - 1, 0));
  const restart = () => { setIndex(0); setPaused(false); };
  const changeAudience = () => { setAudience(null); setIndex(0); setPaused(false); };
  const goFull = () => { if (rootRef.current?.requestFullscreen) rootRef.current.requestFullscreen().catch(() => {}); };
  const backDest = user ? "/segnalazioni" : "/login";
  const leave = () => { if (music.current) music.current.stop(); if (document.fullscreenElement) document.exitFullscreen?.(); navigate(backDest); };
  const exit = leave;

  const Icon = scene?.icon || Building2;
  const isLast = audience && index === scenes.length - 1;

  return (
    <div ref={rootRef} data-testid="presentazione-root"
      className="fixed inset-0 overflow-hidden text-white select-none"
      style={{ background: "radial-gradient(120% 120% at 80% 0%, #7A193D 0%, #5C112C 45%, #2E0916 100%)" }}>
      <div className="pointer-events-none absolute inset-0 opacity-[0.12]"
        style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)", backgroundSize: "3px 3px" }} />
      <motion.div className="pointer-events-none absolute -top-40 -left-40 w-[36rem] h-[36rem] rounded-full"
        style={{ background: "radial-gradient(circle, rgba(201,138,160,0.35), transparent 70%)" }}
        animate={{ x: [0, 40, 0], y: [0, 30, 0] }} transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }} />
      <motion.div className="pointer-events-none absolute -bottom-40 -right-40 w-[40rem] h-[40rem] rounded-full"
        style={{ background: "radial-gradient(circle, rgba(212,175,90,0.18), transparent 70%)" }}
        animate={{ x: [0, -50, 0], y: [0, -20, 0] }} transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }} />

      {/* COVER — scelta del destinatario */}
      {!audience && (
        <div className="relative z-10 h-full overflow-y-auto">
          <div className="min-h-full flex flex-col items-center justify-center text-center px-6 py-10">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="w-full max-w-5xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#E8B4C6]/40 text-[#F0C9D6] text-sm tracking-[0.25em] uppercase mb-6">
              Ginius by Amministrazioni Izzo
            </div>
            <GiniusWordmark className="text-6xl sm:text-7xl lg:text-8xl text-white" smokeTint="rgba(240,201,214,0.5)" />
            <p className="mt-3 text-xl sm:text-2xl text-[#F0C9D6] italic">Tu lo chiedi, lui lo fa!</p>
            <p className="mt-6 text-lg text-[#F0D7E0]/90 max-w-2xl mx-auto">
              Filmati dimostrativi
            </p>
            <div className={`mt-10 grid gap-4 ${audiences.length === 1 ? "max-w-md mx-auto" : "sm:grid-cols-3"}`}>
              {audiences.map((a, i) => {
                const Ai = a.icon;
                return (
                  <motion.button key={a.key} data-testid={`presentazione-audience-${a.key}`}
                    onClick={() => begin(a.key)}
                    initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.12 }}
                    className="group text-left bg-white/8 hover:bg-white/15 border border-white/15 rounded-2xl p-6 transition-colors backdrop-blur-sm">
                    <div className="w-14 h-14 rounded-xl bg-[#D4AF5A]/15 flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
                      <Ai className="w-7 h-7 text-[#F0C9D6]" strokeWidth={1.5} />
                    </div>
                    <div className="font-display text-xl font-bold mb-1">{a.label}</div>
                    <div className="text-base text-[#F0D7E0]/80">{a.desc}</div>
                    <div className="mt-4 inline-flex items-center gap-2 text-[#D4AF5A] font-semibold">
                      Avvia <Play className="w-4 h-4 fill-[#D4AF5A]" />
                    </div>
                  </motion.button>
                );
              })}
            </div>
            <div className="mt-8">
              <button data-testid="presentazione-exit-cover" onClick={leave}
                className="text-[#E8B4C6]/70 hover:text-white text-base underline underline-offset-4">
                {user ? "Torna alle manutenzioni" : "Vai all'accesso"}
              </button>
            </div>
          </motion.div>
          </div>
        </div>
      )}

      {/* SLIDES */}
      {audience && (
        <div className="relative z-10 h-full flex flex-col">
          <div className="absolute top-0 left-0 right-0 flex gap-1.5 p-3">
            {scenes.map((s, i) => (
              <div key={s.id} className="h-1.5 flex-1 rounded-full bg-white/20 overflow-hidden">
                <motion.div className="h-full bg-[#F0C9D6]"
                  initial={false}
                  animate={{ width: i < index ? "100%" : i === index ? (paused ? "40%" : "100%") : "0%" }}
                  transition={{ duration: i === index && !paused ? (s.seconds || 12) : 0.3, ease: "linear" }} />
              </div>
            ))}
          </div>

          <div className="flex-1 flex items-center overflow-y-auto">
            <div className="w-full max-w-7xl mx-auto px-6 sm:px-10 grid lg:grid-cols-2 gap-8 lg:gap-14 items-center">
              <AnimatePresence mode="wait">
                <motion.div key={"txt-" + audience + index}
                  initial={{ opacity: 0, x: -40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 40 }}
                  transition={{ duration: 0.55, ease: "easeOut" }} className="order-2 lg:order-1">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-[#F0C9D6] text-sm font-semibold tracking-wide mb-5">
                    <Icon className="w-4 h-4" /> {scene.eyebrow}
                  </div>
                  <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.1]">{scene.title}</h2>
                  {scene.subtitle && <p className="mt-4 text-lg sm:text-xl text-[#F0D7E0]/90 max-w-xl">{scene.subtitle}</p>}
                  {scene.bullets?.length > 0 && (
                    <ul className="mt-7 space-y-3">
                      {scene.bullets.map((b, i) => (
                        <motion.li key={i} className="flex items-start gap-3 text-lg"
                          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.35 + i * 0.25, duration: 0.5 }}>
                          <span className="mt-1.5 w-2.5 h-2.5 rounded-full bg-[#D4AF5A] shrink-0" />
                          <span className="text-[#F7E9EF]">{b}</span>
                        </motion.li>
                      ))}
                    </ul>
                  )}
                  {scene.cta && (
                    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
                      className="mt-8 flex flex-wrap gap-3">
                      {scene.ctaProva ? (
                        <button data-testid="presentazione-cta-prova" onClick={() => navigate("/richiedi-prova")}
                          className="inline-flex items-center gap-2 bg-[#D4AF5A] text-[#3a0b1d] font-bold text-lg px-7 py-3.5 rounded-full hover:scale-[1.03] transition-transform">
                          Richiedi la prova gratuita <ArrowRight className="w-5 h-5" />
                        </button>
                      ) : (
                        <button data-testid="presentazione-cta-login" onClick={exit}
                          className="inline-flex items-center gap-2 bg-white text-brand font-bold text-lg px-7 py-3.5 rounded-full hover:scale-[1.03] transition-transform">
                          {user ? "Vai alle manutenzioni" : "Entra nella piattaforma"} <ArrowRight className="w-5 h-5" />
                        </button>
                      )}
                      <button data-testid="presentazione-cta-restart" onClick={restart}
                        className="inline-flex items-center gap-2 border border-white/30 text-white font-semibold text-lg px-7 py-3.5 rounded-full hover:bg-white/10 transition-colors">
                        <RotateCcw className="w-5 h-5" /> Rivedi
                      </button>
                      {canChangeAudience && (
                        <button data-testid="presentazione-cta-audience" onClick={changeAudience}
                          className="inline-flex items-center gap-2 border border-white/30 text-white font-semibold text-lg px-7 py-3.5 rounded-full hover:bg-white/10 transition-colors">
                          <Users className="w-5 h-5" /> Cambia pubblico
                        </button>
                      )}
                    </motion.div>
                  )}
                </motion.div>
              </AnimatePresence>

              <AnimatePresence mode="wait">
                <motion.div key={"vis-" + audience + index}
                  initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.6, ease: "easeOut" }} className="order-1 lg:order-2 flex items-center justify-center">
                  {scene.image ? (
                    <div className="relative w-full max-w-xl">
                      <div className="absolute inset-0 rounded-3xl bg-[#D4AF5A]/20 blur-2xl" />
                      <motion.img src={scene.image} alt={scene.title}
                        className="relative w-full rounded-3xl shadow-2xl ring-1 ring-white/10"
                        animate={{ scale: paused ? 1 : [1, 1.05] }}
                        transition={{ duration: (scene.seconds || 12), ease: "linear" }} />
                    </div>
                  ) : (
                    <motion.div className="relative w-56 h-56 sm:w-72 sm:h-72 rounded-[2rem] bg-white/8 border border-white/15 flex items-center justify-center backdrop-blur-sm"
                      animate={{ y: [0, -12, 0] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}>
                      <div className="absolute inset-0 rounded-[2rem] bg-[#D4AF5A]/10 blur-xl" />
                      <Icon className="relative w-28 h-28 sm:w-36 sm:h-36 text-[#F0C9D6]" strokeWidth={1.3} />
                    </motion.div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          <div className="relative z-20 pb-6 px-6">
            <div className="mx-auto max-w-md flex items-center justify-center gap-2 bg-black/25 backdrop-blur-md rounded-full px-3 py-2 border border-white/10">
              <Ctrl testid="presentazione-back" onClick={leave}><ArrowLeft className="w-5 h-5" /></Ctrl>
              <div className="w-px h-6 bg-white/15 mx-1" />
              <Ctrl testid="presentazione-prev" onClick={prev} disabled={index === 0}><SkipBack className="w-5 h-5" /></Ctrl>
              <Ctrl testid="presentazione-playpause" onClick={() => setPaused((p) => !p)}>
                {paused ? <Play className="w-6 h-6" /> : <Pause className="w-6 h-6" />}
              </Ctrl>
              <Ctrl testid="presentazione-next" onClick={next} disabled={isLast}><SkipForward className="w-5 h-5" /></Ctrl>
              <div className="w-px h-6 bg-white/15 mx-1" />
              <Ctrl testid="presentazione-mute" onClick={toggleMute}>{muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}</Ctrl>
              {canChangeAudience && <Ctrl testid="presentazione-audience" onClick={changeAudience}><Users className="w-5 h-5" /></Ctrl>}
              <Ctrl testid="presentazione-fullscreen" onClick={goFull}><Maximize2 className="w-5 h-5" /></Ctrl>
              <Ctrl testid="presentazione-exit" onClick={exit}><X className="w-5 h-5" /></Ctrl>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Ctrl({ children, onClick, disabled, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled}
      className="w-11 h-11 flex items-center justify-center rounded-full text-white hover:bg-white/15 disabled:opacity-30 disabled:hover:bg-transparent transition-colors">
      {children}
    </button>
  );
}
